import React from 'react';
import { ReactEditor, useSlateStatic } from 'slate-react';
import { Transforms } from 'slate';
import {
  type ComponentProps,
  type EditorPlugin,
  type VideoElement,
} from '../../../types/aha-editor';
import MediaCommon from '../MediaCommon';
import styles from './ImageComponentPlugin.module.css';
import Resizer from '../../../components/Resizer/Resizer';
import { type DragHandlePosition } from '../../../components/Resizer/DragHandle';
import {
  withImagePosition,
  ToolPositionInline,
  ToolPositionBlock,
} from '../tools/ToolPosition';
import ToolDelete from '../../../tools/ToolDelete';
import ToolImageViewer from '../tools/ToolImageViewer';
import ToolEditAlt from '../tools/ToolEditAlt';
import SelectionRectangle from '../../../components/SelectionRectangle/SelectionRectangle';
import useDetectElementSize from './useDetectElementSize';

const IMAGE_PLACEHOLDER = `<svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="image" class="svg-inline--fa fa-image" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="#dddddd" d="M152 120c-26.51 0-48 21.49-48 48s21.49 48 48 48s48-21.49 48-48S178.5 120 152 120zM447.1 32h-384C28.65 32-.0091 60.65-.0091 96v320c0 35.35 28.65 64 63.1 64h384c35.35 0 64-28.65 64-64V96C511.1 60.65 483.3 32 447.1 32zM463.1 409.3l-136.8-185.9C323.8 218.8 318.1 216 312 216c-6.113 0-11.82 2.768-15.21 7.379l-106.6 144.1l-37.09-46.1c-3.441-4.279-8.934-6.809-14.77-6.809c-5.842 0-11.33 2.529-14.78 6.809l-75.52 93.81c0-.0293 0 .0293 0 0L47.99 96c0-8.822 7.178-16 16-16h384c8.822 0 16 7.178 16 16V409.3z"></path></svg>`;

// Render a video element.
//
// Note that this element does not work reliably with copy. The reason is that
// clicking into the video player steals focus from the editor and then the <video>
// element captures keyboard events. The general solution would be to render our
// own video player controls.

const Video: React.FC<ComponentProps<VideoElement>> = ({
  element,
  children,
}) => {
  const { media_src: src, media_position: position, media_alt: alt } = element;
  const editor = useSlateStatic();

  const { detectElementSize, width, height, originalWidth, originalHeight } =
    useDetectElementSize(element);

  let imageUrl: string | undefined;
  if (MediaCommon.isTemporaryDataUrl(src)) {
    imageUrl = MediaCommon.getTemporaryData(src);
    if (!imageUrl) {
      // The image is being uploaded by a collaborator, or the upload failed
      // and our page was reloaded. Display a placeholder image instead.
      imageUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
        IMAGE_PLACEHOLDER
      )}`;
    }
  } else {
    imageUrl = src;
  }

  let handles: DragHandlePosition[] = [];
  if (position === 'inline') {
    handles = ['e'];
  } else {
    handles = ['w', 'e'];
  }

  const selectNode = (event: React.MouseEvent) => {
    const path = ReactEditor.findPath(editor, element);
    Transforms.select(editor, { path: [...path, 0], offset: 0 });
    event.preventDefault();
  };

  return (
    <>
      <SelectionRectangle>
        <Resizer
          width={width}
          height={height}
          originalWidth={originalWidth}
          originalHeight={originalHeight}
          element={element}
          handles={handles}
          keepRatio
          showPixelSize
          liveResize
        >
          <span
            className={styles.image}
            style={{
              display: 'inline-block',
              position: 'relative',
              maxWidth: '100%',
              textAlign: 'center',
            }}
          >
            <video
              controls
              src={imageUrl}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
              }}
              width={originalWidth}
              height={originalHeight}
              onClick={selectNode}
              onDoubleClick={(event) => {
                if (!(event.target instanceof HTMLVideoElement)) {
                  return;
                }

                event.target.play();
              }}
              aria-label={alt}
              ref={detectElementSize}
            />
          </span>
          {children}
        </Resizer>
      </SelectionRectangle>
    </>
  );
};

const VideoComponentPlugin: EditorPlugin = {
  name: 'Video component plugin',
  component: {
    type: 'video',
    component: withImagePosition(Video),
    toolbarItems: [
      [ToolImageViewer],
      [ToolPositionBlock, ToolPositionInline],
      [ToolEditAlt],
      [ToolDelete],
    ],
  },
};

export default VideoComponentPlugin;
